import { FC, memo, useCallback, useState } from 'react'

import { ArrowLeftIcon, ArrowRightIcon } from '@cash-me/react-components'
import NextImage from 'next/image'
import type { Swiper as SwiperType } from 'swiper/types'

import { Carousel } from 'components/carousel'
import { Grid, Col } from 'components/grid'
import { IconInstagram, IconLinkedin, IconTiktok, IconYoutube } from 'components/logo/svg'
import { SectionLabel } from 'components/section-label'
import { theme } from 'styles/theme'
import { pushDataLayer } from 'utils/push-data-layer'

import { SocialCard, THUMB_SIZES } from './card'
import { socialMediaPlatforms } from './data'
import { SocialPlatform } from './social-media.model'
import * as S from './styles'

type SocialIcon = FC<{ color?: string; className?: string }>

const platformIcons: Record<SocialPlatform, SocialIcon> = {
  youtube: IconYoutube,
  instagram: IconInstagram,
  tiktok: IconTiktok,
  linkedin: IconLinkedin
}

const SocialMedia = memo(() => {
  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const activePlatform = socialMediaPlatforms[activeIndex]

  const handleSelect = useCallback((index: number, label: string) => {
    setActiveIndex(index)
    pushDataLayer({ category: 'Clique', action: 'Redes Sociais', label: `Botão - ${label}` })
  }, [])

  const handlePlay = useCallback(
    (videoTitle: string) => {
      pushDataLayer({
        category: 'Clique',
        action: 'Redes Sociais',
        label: `Play - ${activePlatform.label} - ${videoTitle}`
      })
    },
    [activePlatform.label]
  )

  const handlePrev = useCallback(() => {
    if (swiper) swiper.slidePrev()
  }, [swiper])

  const handleNext = useCallback(() => {
    if (swiper) swiper.slideNext()
  }, [swiper])

  return (
    <S.Section>
      <Grid col={{ phone: 1, tablet: 1, desktop: 1 }}>
        <Col>
          <S.Content>
            <S.Header>
              <SectionLabel
                size="body1"
                weight="bold"
                color="white"
                barColor="var(--colors-blue-background-strong-default)"
              >
                Nossas redes
              </SectionLabel>
            </S.Header>

            <S.Controls>
              <S.SocialButtons>
                {socialMediaPlatforms.map((platform, index) => {
                  const PlatformIcon = platformIcons[platform.platform]
                  const isActive = activeIndex === index

                  return (
                    <S.SocialButton
                      key={platform.platform}
                      type="button"
                      $active={isActive}
                      aria-pressed={isActive}
                      aria-label={`Ver vídeos do ${platform.label}`}
                      onClick={() => handleSelect(index, platform.label)}
                    >
                      <PlatformIcon
                        color={isActive ? theme.colors['cashme-dark-blue'] : 'var(--colors-cyan-text-contrast)'}
                      />
                    </S.SocialButton>
                  )
                })}
              </S.SocialButtons>

              <S.Arrows>
                <S.Arrow type="button" aria-label="Vídeo anterior" onClick={handlePrev}>
                  <ArrowLeftIcon size={24} color="var(--colors-cyan-text-contrast)" />
                </S.Arrow>
                <S.Arrow type="button" aria-label="Próximo vídeo" onClick={handleNext}>
                  <ArrowRightIcon size={24} color="var(--colors-cyan-text-contrast)" />
                </S.Arrow>
              </S.Arrows>
            </S.Controls>

            <S.CarouselWrapper>
              <Carousel
                key={activePlatform.platform}
                navigation={false}
                setOutsideSwiperRef={setSwiper}
                swiperOptions={{
                  slidesPerView: 'auto',
                  spaceBetween: 16,
                  rewind: true,
                  slidesOffsetBefore: 48,
                  slidesOffsetAfter: 48,
                  breakpoints: { 768: { slidesOffsetBefore: 0, slidesOffsetAfter: 0 } }
                }}
              >
                {activePlatform.videos.map((video, index) => (
                  <SocialCard
                    key={`${activePlatform.platform}-${index}`}
                    label={activePlatform.label}
                    {...video}
                    onPlay={() => handlePlay(video.videoTitle)}
                  />
                ))}
              </Carousel>

              <S.Warmer aria-hidden>
                {socialMediaPlatforms
                  .filter(({ platform }) => platform !== activePlatform.platform)
                  .flatMap(({ videos }) => videos)
                  .map(({ thumbnail }) => (
                    <NextImage key={thumbnail} src={thumbnail} alt="" fill sizes={THUMB_SIZES} />
                  ))}
              </S.Warmer>
            </S.CarouselWrapper>
          </S.Content>
        </Col>
      </Grid>
    </S.Section>
  )
})

SocialMedia.displayName = 'SocialMedia'

export default SocialMedia
